/**
 * Walkthrough Modal Positioning Utilities
 * Functions to calculate and apply dynamic positioning for walkthrough modals
 */

/**
 * Calculate modal position based on highlighted tab location
 * @param {HTMLElement} tabElement - The highlighted tab element
 * @param {boolean} isMobile - Whether we're on mobile view
 * @returns {Object} - Position styles object with arrow offset
 */
export const calculateModalPosition = (tabElement, isMobile = false) => {
  if (!tabElement) {
    // Fallback to center positioning
    return {
      position: 'center',
      styles: {},
      arrowOffset: null
    };
  }

  const rect = tabElement.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const modalWidth = 400; // Max width from design
  const modalHeight = 300; // Estimated height
  const spacing = 24; // Spacing from tab

  if (isMobile) {
    // Position above bottom nav on mobile with arrow pointing down to nav
    const bottomNavHeight = 64; // Height of bottom nav
    const spaceAboveNav = viewportHeight - rect.top;
    
    // Check if there's enough space above the bottom nav
    if (spaceAboveNav > modalHeight + spacing) {
      return {
        position: 'above-mobile',
        styles: {
          position: 'fixed',
          bottom: `${bottomNavHeight + spacing}px`,
          left: '50%',
          transform: 'translateX(-50%)',
          maxWidth: 'calc(100vw - 2rem)',
          width: '90%'
        },
        arrowOffset: null // Centered arrow for mobile
      };
    }
    
    // Fallback to center if not enough space
    return {
      position: 'center',
      styles: {},
      arrowOffset: null
    };
  } else {
    // Position below the navigation item on desktop with arrow pointing up
    const spaceBelowTab = viewportHeight - rect.bottom;
    
    // Check if there's enough space below the tab
    if (spaceBelowTab > modalHeight + spacing) {
      // Calculate horizontal position to avoid overflow
      let leftPosition = rect.left;
      let arrowOffset = 24; // Default arrow position from left edge
      
      // Check if modal would overflow right edge
      const wouldOverflowRight = leftPosition + modalWidth > viewportWidth - spacing;
      
      if (wouldOverflowRight) {
        // Calculate how far we need to shift the modal left
        const originalLeft = rect.left;
        leftPosition = Math.max(spacing, Math.min(rect.right - modalWidth, viewportWidth - modalWidth - spacing));
        
        // Calculate arrow offset to still point at the button center
        const buttonCenter = rect.left + (rect.width / 2);
        arrowOffset = buttonCenter - leftPosition;
        
        // Clamp arrow position to reasonable range (10px to modalWidth - 10px)
        arrowOffset = Math.max(10, Math.min(arrowOffset, modalWidth - 10));
      }
      
      return {
        position: 'below',
        styles: {
          position: 'fixed',
          top: `${rect.bottom + spacing}px`,
          left: `${leftPosition}px`,
          maxWidth: `${modalWidth}px`,
          width: 'auto'
        },
        arrowOffset: arrowOffset
      };
    }
    
    // Fallback to center if not enough space
    return {
      position: 'center',
      styles: {},
      arrowOffset: null
    };
  }
};

/**
 * Get arrow direction based on modal position
 * @param {string} position - Position type ('below', 'above-mobile', 'center')
 * @returns {string} - Arrow direction class
 */
export const getArrowDirection = (position) => {
  switch (position) {
    case 'below':
      return 'positioned-below'; // Arrow pointing up to navigation tab
    case 'above-mobile':
      return 'positioned-above-mobile'; // Arrow pointing down to bottom nav on mobile
    case 'center':
    default:
      return ''; // No arrow for centered modals
  }
};

/**
 * Apply positioning styles to modal element
 * @param {HTMLElement} modalElement - The modal element to position
 * @param {Object} positionData - Position data from calculateModalPosition
 */
export const applyModalPosition = (modalElement, positionData) => {
  if (!modalElement || !positionData) return;

  const { position, styles, arrowOffset } = positionData;

  if (position === 'center') {
    // Remove any custom positioning for center
    modalElement.style.position = '';
    modalElement.style.top = '';
    modalElement.style.left = '';
    modalElement.style.bottom = '';
    modalElement.style.transform = '';
    modalElement.style.maxWidth = '';
    modalElement.style.width = '';
    modalElement.style.removeProperty('--arrow-offset');
  } else {
    // Apply custom positioning
    Object.keys(styles).forEach(key => {
      modalElement.style[key] = styles[key];
    });
    
    // Apply arrow offset if provided
    if (arrowOffset !== null && arrowOffset !== undefined) {
      modalElement.style.setProperty('--arrow-offset', `${arrowOffset}px`);
    } else {
      modalElement.style.removeProperty('--arrow-offset');
    }
  }
};

/**
 * Handle viewport resize and reposition modal
 * @param {HTMLElement} modalElement - The modal element
 * @param {string} tabName - Name of the highlighted tab
 * @param {Function} callback - Callback to execute after repositioning
 */
export const handleViewportResize = (modalElement, tabName, callback) => {
  if (!modalElement || !tabName) return;

  const isMobile = window.innerWidth < 768;
  const tabElement = document.querySelector(`[data-walkthrough-tab="${tabName}"]`);
  
  if (tabElement) {
    const positionData = calculateModalPosition(tabElement, isMobile);
    applyModalPosition(modalElement, positionData);
    
    if (callback) {
      callback(positionData);
    }
  }
};
